/**
 * EmbalajesSection — perfiles de embalaje y accesorios (MVP-02C §3.4).
 *
 * Un perfil activo no se edita en caliente (podría cambiar bajo una
 * cotización vigente sin pagar): la vía segura es duplicar, editar la copia
 * y activarla. El backend aplica esa regla; aquí solo se ofrece el camino.
 */
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Copy, Plus, RotateCw, Trash2 } from "lucide-react";

import { TextInput, confirmDelete, showErrorAlert } from "../../../../../components/ui/index.js";
import {
    createPackagingProfile, duplicatePackagingProfile, fetchPackagingProfiles,
    parseAdminErrors, setPackagingProfileActive, updatePackagingProfile,
} from "../../../../../domain/shipping-admin/api.js";

const firstErrorMessage = (parsed) =>
    parsed.detail || parsed.general || Object.values(parsed)[0] || "No se pudo completar la operación.";

const FORM_VACIO = {
    name: "", tare_grams: 0,
    inner_length_mm: "", inner_width_mm: "", inner_height_mm: "",
    outer_length_mm: "", outer_width_mm: "", outer_height_mm: "",
    max_content_weight_grams: "", cost: "0.00", currency: "EUR",
    accessories: [],
};

const Campo = ({ id, label, children, error, suffix }) => (
    <div className="form-control gap-1">
        <label className="label py-0" htmlFor={id}>
            <span className="label-text text-xs font-semibold">{label}{suffix && ` (${suffix})`}</span>
        </label>
        {children}
        {error && <span role="alert" className="label-text-alt text-error">{error}</span>}
    </div>
);
Campo.propTypes = {
    id: PropTypes.string.isRequired, label: PropTypes.string.isRequired,
    children: PropTypes.node, error: PropTypes.string, suffix: PropTypes.string,
};

function ProfileForm({ initial, editingId, onSaved, onCancel }) {
    const [value, setValue] = useState(initial || FORM_VACIO);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const setField = (field, v) => {
        setValue((prev) => ({ ...prev, [field]: v }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const setAccessory = (index, field, v) => {
        setValue((prev) => ({
            ...prev,
            accessories: prev.accessories.map((a, i) => (i === index ? { ...a, [field]: v } : a)),
        }));
    };
    const addAccessory = () =>
        setValue((prev) => ({ ...prev, accessories: [...prev.accessories, { name: "", added_grams: 0 }] }));
    const removeAccessory = (index) =>
        setValue((prev) => ({ ...prev, accessories: prev.accessories.filter((_, i) => i !== index) }));

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        const payload = {
            ...value,
            tare_grams: Number(value.tare_grams) || 0,
            inner_length_mm: Number(value.inner_length_mm) || 0,
            inner_width_mm: Number(value.inner_width_mm) || 0,
            inner_height_mm: Number(value.inner_height_mm) || 0,
            outer_length_mm: Number(value.outer_length_mm) || 0,
            outer_width_mm: Number(value.outer_width_mm) || 0,
            outer_height_mm: Number(value.outer_height_mm) || 0,
            max_content_weight_grams: value.max_content_weight_grams === ""
                ? null : Number(value.max_content_weight_grams),
            accessories: value.accessories.map((a) => ({ ...a, added_grams: Number(a.added_grams) || 0 })),
        };
        try {
            if (editingId) {
                await updatePackagingProfile(editingId, payload);
            } else {
                await createPackagingProfile(payload);
            }
            onSaved();
        } catch (error) {
            setErrors(parseAdminErrors(error));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="rounded-xl border border-base-300 bg-base-200 p-4 flex flex-col gap-4">
            <h3 className="font-semibold text-sm">{editingId ? "Editar borrador" : "Nuevo perfil de embalaje"}</h3>
            {errors.general && <p role="alert" className="text-sm text-error">{errors.general}</p>}

            <Campo id="pkg-name" label="Nombre" error={errors.name}>
                <TextInput id="pkg-name" value={value.name} onChange={(e) => setField("name", e.target.value)}
                    placeholder="Caja M" />
            </Campo>

            <div>
                <p className="text-xs uppercase tracking-widest text-base-content/50 font-semibold mb-2">
                    Dimensiones interiores (mm)
                </p>
                <div className="grid grid-cols-3 gap-3">
                    <Campo id="pkg-il" label="Largo" suffix="mm" error={errors.inner_length_mm}>
                        <TextInput id="pkg-il" type="number" min="0" value={value.inner_length_mm}
                            onChange={(e) => setField("inner_length_mm", e.target.value)} />
                    </Campo>
                    <Campo id="pkg-iw" label="Ancho" suffix="mm" error={errors.inner_width_mm}>
                        <TextInput id="pkg-iw" type="number" min="0" value={value.inner_width_mm}
                            onChange={(e) => setField("inner_width_mm", e.target.value)} />
                    </Campo>
                    <Campo id="pkg-ih" label="Alto" suffix="mm" error={errors.inner_height_mm}>
                        <TextInput id="pkg-ih" type="number" min="0" value={value.inner_height_mm}
                            onChange={(e) => setField("inner_height_mm", e.target.value)} />
                    </Campo>
                </div>
            </div>

            <div>
                <p className="text-xs uppercase tracking-widest text-base-content/50 font-semibold mb-2">
                    Dimensiones exteriores (mm)
                </p>
                <div className="grid grid-cols-3 gap-3">
                    <Campo id="pkg-ol" label="Largo" suffix="mm" error={errors.outer_length_mm}>
                        <TextInput id="pkg-ol" type="number" min="0" value={value.outer_length_mm}
                            onChange={(e) => setField("outer_length_mm", e.target.value)} />
                    </Campo>
                    <Campo id="pkg-ow" label="Ancho" suffix="mm" error={errors.outer_width_mm}>
                        <TextInput id="pkg-ow" type="number" min="0" value={value.outer_width_mm}
                            onChange={(e) => setField("outer_width_mm", e.target.value)} />
                    </Campo>
                    <Campo id="pkg-oh" label="Alto" suffix="mm" error={errors.outer_height_mm}>
                        <TextInput id="pkg-oh" type="number" min="0" value={value.outer_height_mm}
                            onChange={(e) => setField("outer_height_mm", e.target.value)} />
                    </Campo>
                </div>
                <p className="text-xs text-base-content/50 mt-1">
                    Deben cubrir las interiores en alguna orientación.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Campo id="pkg-tare" label="Tara (embalaje vacío)" suffix="g" error={errors.tare_grams}>
                    <TextInput id="pkg-tare" type="number" min="0" value={value.tare_grams}
                        onChange={(e) => setField("tare_grams", e.target.value)} />
                </Campo>
                <Campo id="pkg-max" label="Peso máx. del contenido" suffix="g, vacío = sin límite"
                    error={errors.max_content_weight_grams}>
                    <TextInput id="pkg-max" type="number" min="0" value={value.max_content_weight_grams}
                        onChange={(e) => setField("max_content_weight_grams", e.target.value)} />
                </Campo>
                <Campo id="pkg-cost" label="Coste del embalaje" suffix="EUR" error={errors.cost}>
                    <TextInput id="pkg-cost" type="number" min="0" step="0.01" value={value.cost}
                        onChange={(e) => setField("cost", e.target.value)} />
                </Campo>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-widest text-base-content/50 font-semibold">
                        Accesorios (peso añadido)
                    </p>
                    <button type="button" onClick={addAccessory} className="btn btn-ghost btn-xs gap-1">
                        <Plus className="w-3 h-3" /> Añadir
                    </button>
                </div>
                {value.accessories.length === 0 ? (
                    <p className="text-xs text-base-content/50">Sin accesorios.</p>
                ) : (
                    <div className="flex flex-col gap-2">
                        {value.accessories.map((accessory, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <TextInput
                                    aria-label={`Nombre del accesorio ${index + 1}`}
                                    value={accessory.name}
                                    onChange={(e) => setAccessory(index, "name", e.target.value)}
                                    placeholder="Relleno" className="flex-1" inputSize="sm"
                                />
                                <TextInput
                                    aria-label={`Peso del accesorio ${index + 1} en gramos`}
                                    type="number" min="0" value={accessory.added_grams}
                                    onChange={(e) => setAccessory(index, "added_grams", e.target.value)}
                                    className="w-24" inputSize="sm"
                                />
                                <span className="text-xs text-base-content/50">g</span>
                                <button type="button" onClick={() => removeAccessory(index)}
                                    className="btn btn-ghost btn-xs btn-square" aria-label="Quitar accesorio">
                                    <Trash2 className="w-3.5 h-3.5 text-error" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex gap-2">
                <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                    {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear como borrador"}
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>Cancelar</button>
            </div>
        </form>
    );
}
ProfileForm.propTypes = {
    initial: PropTypes.object, editingId: PropTypes.number,
    onSaved: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired,
};

export default function EmbalajesSection({ onChanged }) {
    const [recurso, setRecurso] = useState({ state: "loading", data: [] });
    const [formState, setFormState] = useState(null); // null | { editingId, initial }
    const [busyId, setBusyId] = useState(null);

    const cargar = useCallback(() => {
        setRecurso((prev) => ({ ...prev, state: "loading" }));
        return fetchPackagingProfiles()
            .then((data) => setRecurso({ state: "ready", data }))
            .catch(() => setRecurso({ state: "error", data: [] }));
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const handleSaved = () => {
        setFormState(null);
        cargar();
        onChanged();
    };

    const toggleActive = async (profile) => {
        const activating = !profile.is_active;
        const ok = await confirmDelete({
            title: activating ? `¿Activar «${profile.name}»?` : `¿Desactivar «${profile.name}»?`,
            text: activating
                ? "El cotizador podrá elegir este perfil para pedidos nuevos de inmediato."
                : "Dejará de estar disponible para pedidos nuevos. Los pedidos ya cotizados no cambian.",
            confirmButtonText: activating ? "Activar" : "Desactivar",
        });
        if (!ok) return;

        setBusyId(profile.id);
        try {
            await setPackagingProfileActive(profile.id, activating);
            cargar();
            onChanged();
        } catch (error) {
            showErrorAlert({ text: firstErrorMessage(parseAdminErrors(error)) });
        } finally {
            setBusyId(null);
        }
    };

    const duplicate = async (profile) => {
        setBusyId(profile.id);
        try {
            await duplicatePackagingProfile(profile.id);
            cargar();
        } catch (error) {
            showErrorAlert({ text: firstErrorMessage(parseAdminErrors(error)) });
        } finally {
            setBusyId(null);
        }
    };

    if (recurso.state === "loading") {
        return (
            <div className="flex flex-col gap-2" aria-busy="true">
                <div className="h-16 rounded-lg bg-base-300/60 animate-pulse" />
                <div className="h-16 rounded-lg bg-base-300/60 animate-pulse" />
                <span className="sr-only">Cargando embalajes…</span>
            </div>
        );
    }

    if (recurso.state === "error") {
        return (
            <div role="alert" className="flex flex-col items-start gap-3 py-6">
                <p className="text-sm text-error">No se pudieron cargar los embalajes.</p>
                <button type="button" className="btn btn-sm gap-1" onClick={cargar}>
                    <RotateCw className="w-4 h-4" /> Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-base-100 border border-base-300 shadow-sm p-4 flex flex-col gap-4">
            {recurso.data.length === 0 ? (
                <p className="text-sm text-base-content/50">Todavía no hay ningún perfil de embalaje.</p>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-base-300">
                    <table className="table table-sm table-zebra">
                        <thead>
                            <tr>
                                <th>Nombre</th><th>Interior (mm)</th><th>Exterior (mm)</th>
                                <th>Tara</th><th>Coste</th><th>Estado</th><th />
                            </tr>
                        </thead>
                        <tbody>
                            {recurso.data.map((profile) => (
                                <tr key={profile.id}>
                                    <td>{profile.name}</td>
                                    <td className="text-xs">
                                        {profile.inner_length_mm}×{profile.inner_width_mm}×{profile.inner_height_mm}
                                    </td>
                                    <td className="text-xs">
                                        {profile.outer_length_mm}×{profile.outer_width_mm}×{profile.outer_height_mm}
                                    </td>
                                    <td className="text-xs">{profile.tare_grams} g</td>
                                    <td className="text-xs">{profile.cost} {profile.currency}</td>
                                    <td>
                                        {profile.is_active
                                            ? <span className="badge badge-success badge-sm">Activo</span>
                                            : <span className="badge badge-ghost badge-sm">Inactivo</span>}
                                    </td>
                                    <td className="text-right whitespace-nowrap">
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                type="button" className="btn btn-ghost btn-xs btn-square"
                                                title="Duplicar" disabled={busyId === profile.id}
                                                onClick={() => duplicate(profile)}
                                            >
                                                <Copy className="w-3.5 h-3.5" />
                                            </button>
                                            {!profile.is_active && (
                                                <button
                                                    type="button" className="btn btn-ghost btn-xs"
                                                    onClick={() => setFormState({ editingId: profile.id, initial: profile })}
                                                >
                                                    Editar
                                                </button>
                                            )}
                                            <button
                                                type="button" className="btn btn-xs btn-outline"
                                                disabled={busyId === profile.id}
                                                onClick={() => toggleActive(profile)}
                                            >
                                                {profile.is_active ? "Desactivar" : "Activar"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {formState ? (
                <ProfileForm
                    key={formState.editingId || "new"}
                    initial={formState.initial ? { ...formState.initial, accessories: formState.initial.accessories || [] } : null}
                    editingId={formState.editingId}
                    onSaved={handleSaved}
                    onCancel={() => setFormState(null)}
                />
            ) : (
                <button type="button" onClick={() => setFormState({ editingId: null, initial: null })} className="btn btn-outline btn-sm gap-1 self-start">
                    <Plus className="w-4 h-4" /> Nuevo perfil de embalaje
                </button>
            )}
        </div>
    );
}

EmbalajesSection.propTypes = { onChanged: PropTypes.func.isRequired };
